import { Box, Button, Container, FormControl, RadioGroup, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { DownloadFileNameFormatOptions } from "../../../../api/types/investorPortalTypes";
import useClientContext from "../../../../context/useClientContext";
import BorderedRadioLabel from "../../../common/BorderedRadioLabel";

const updateInvestorPortalSettings = withErrorHandling(adminApi.updateInvestorPortalSettings);

const InvestorDocumentsSettings = () => {
  const { hasPermissions } = useClientContext();
  const { sendNotificationError } = useNotificationContext();

  const [downloadFileNameFormat, setDownloadFileNameFormat] = useState<DownloadFileNameFormatOptions | null>(null);

  const [isSaving, setIsSaving] = useState(false);

  const [resp, fetchError, { setData: setSettings }] = useFetch(adminApi.getInvestorPortalSettings, {
    onSetData: (data) => setDownloadFileNameFormat(data.settings.downloadFileNameFormat ?? null),
  });

  const handleSaveClick = async () => {
    if (downloadFileNameFormat === null) {
      return;
    }

    setIsSaving(true);
    const [resp, error] = await updateInvestorPortalSettings({ downloadFileNameFormat });
    setIsSaving(false);

    if (error) {
      logError(error, "[InvestorDocumentsSettings] updateInvestorPortalSettings");
      sendNotificationError(error.message);
      return;
    }

    setSettings(resp);
  };

  if (fetchError) {
    return <DataLoadingFailed />;
  }

  if (resp === undefined) {
    return <InlineLoader />;
  }

  const { settings } = resp;
  const canEditSettings = hasPermissions(["ManageInvestorPortalSettings"]);
  const saveDisabled = downloadFileNameFormat === null || downloadFileNameFormat === settings.downloadFileNameFormat;

  return (
    <Container maxWidth="md" disableGutters>
      <Stack spacing={3} sx={{ py: 2.5 }}>
        <Stack spacing={1}>
          <Typography variant="subtitle1">Download File Name Format</Typography>
          <Typography color="textSecondary">
            You can change the file name for the documents that is used when the user downloads them.
          </Typography>
        </Stack>
        <FormControl>
          <RadioGroup
            value={downloadFileNameFormat}
            onChange={({ target }) => setDownloadFileNameFormat(target.value as DownloadFileNameFormatOptions)}
            sx={{ rowGap: 2 }}
          >
            <BorderedRadioLabel<DownloadFileNameFormatOptions>
              value="OriginalFileName"
              disabled={!canEditSettings}
              selectedValue={downloadFileNameFormat}
              sx={{ pl: 2, py: 1 }}
              label={
                <Box>
                  <Typography variant="subtitle2">Use original file name</Typography>
                  <Typography color="textSecondary">
                    Downloads will use the same name that was set during the upload.
                  </Typography>
                </Box>
              }
            />
            <BorderedRadioLabel<DownloadFileNameFormatOptions>
              value="FundInvestorDocument"
              disabled={!canEditSettings}
              selectedValue={downloadFileNameFormat}
              sx={{ pl: 2, py: 1 }}
              label={
                <Box>
                  <Typography variant="subtitle2">Use predefined format</Typography>
                  <Typography color="textSecondary">Downloads will follow a predefined format:</Typography>
                  <Typography>[Fund Name]-[Investor Name]-[Document Name]</Typography>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>
        {canEditSettings && (
          <Button
            loading={isSaving}
            variant="contained"
            color="primary"
            onClick={handleSaveClick}
            disabled={saveDisabled}
            sx={{ width: "5rem" }}
          >
            Save
          </Button>
        )}
      </Stack>
    </Container>
  );
};

export default InvestorDocumentsSettings;
